import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle } from '../theme';

/**
 * Doctor master — list, create, edit, deactivate.
 *
 * Used inside Settings page as a sub-section.
 */
const DoctorMasterPage = ({ API_BASE, branches, refreshDoctors }) => {
  const [doctors, setDoctors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [doctorStats, setDoctorStats] = useState({});  // J1 — name → stats

  useEffect(() => {
    axios.get(`${API_BASE}/outcomes/stats/by-doctor?minSample=1`)
      .then(r => {
        const map = {};
        (r.data || []).forEach(s => { map[s.doctorName] = s; });
        setDoctorStats(map);
      }).catch(() => {});
  }, [API_BASE]);

  function emptyForm() {
    return {
      name: '', qualification: '', specialty: '', registrationNumber: '',
      phone: '', email: '', color: '#00B5B8', commissionPercent: 0,
      branchId: '', isActive: true,
    };
  }

  const fetchDoctors = async () => {
    try {
      const r = await axios.get(`${API_BASE}/doctors`);
      setDoctors(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchDoctors(); }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Doctor name is required');
    try {
      if (editing) {
        await axios.put(`${API_BASE}/doctors/${editing._id}`, form);
      } else {
        await axios.post(`${API_BASE}/doctors`, form);
      }
      setForm(emptyForm());
      setEditing(null);
      fetchDoctors();
      refreshDoctors && refreshDoctors();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '',
      qualification: d.qualification || '',
      specialty: d.specialty || '',
      registrationNumber: d.registrationNumber || '',
      phone: d.phone || '',
      email: d.email || '',
      color: d.color || '#00B5B8',
      commissionPercent: d.commissionPercent || 0,
      branchId: d.branchId || '',
      isActive: d.isActive !== false,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this doctor?')) return;
    await axios.delete(`${API_BASE}/doctors/${id}`);
    fetchDoctors();
    refreshDoctors && refreshDoctors();
  };

  const handleCancel = () => { setEditing(null); setForm(emptyForm()); };

  return (
    <div style={cardStyle}>
      <h3 style={{color:theme.navy, margin:'0 0 14px'}}>👨‍⚕️ Doctors</h3>

      {/* Form */}
      <div style={{backgroundColor:'#f8f9fa', borderRadius:'10px', padding:'18px', marginBottom:'20px'}}>
        <h4 style={{color:'#0582ca', margin:'0 0 14px'}}>{editing ? 'Edit Doctor' : '+ Add New Doctor'}</h4>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Dr. Full Name" style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Qualification</label>
            <input value={form.qualification} onChange={e=>setForm({...form, qualification:e.target.value})} placeholder="BDS, MDS" style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Specialty</label>
            <input value={form.specialty} onChange={e=>setForm({...form, specialty:e.target.value})} placeholder="Endodontist" style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Reg. Number</label>
            <input value={form.registrationNumber} onChange={e=>setForm({...form, registrationNumber:e.target.value})} style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input value={form.email} onChange={e=>setForm({...form, email:e.target.value})} style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Branch</label>
            <select value={form.branchId} onChange={e=>setForm({...form, branchId:e.target.value})} style={inputStyle}>
              <option value="">— Select —</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Calendar Colour</label>
            <input type="color" value={form.color} onChange={e=>setForm({...form, color:e.target.value})} style={{...inputStyle, height:'40px', padding:'4px'}}/>
          </div>
          <div>
            <label style={labelStyle}>Commission %</label>
            <input type="number" value={form.commissionPercent} onChange={e=>setForm({...form, commissionPercent:e.target.value})} style={inputStyle}/>
          </div>
        </div>
        <div style={{display:'flex', gap:'10px'}}>
          <button onClick={handleSave} style={{...btnStyle, marginTop:0}}>{editing ? 'Update' : 'Add Doctor'}</button>
          {editing && <button onClick={handleCancel} style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d'}}>Cancel</button>}
        </div>
      </div>

      {/* List */}
      <table style={{width:'100%', borderCollapse:'collapse'}}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Qualification</th>
            <th style={thStyle}>Specialty</th>
            <th style={thStyle}>Phone</th>
            <th style={thStyle}>Branch</th>
            <th style={thStyle}>Colour</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Success Rate</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {doctors.map(d => {
            const branch = branches.find(b => b._id === d.branchId);
            const stats = doctorStats[d.name];
            return (
              <tr key={d._id}>
                <td style={tdStyle}><strong>{d.name}</strong></td>
                <td style={tdStyle}>{d.qualification || '—'}</td>
                <td style={tdStyle}>{d.specialty || '—'}</td>
                <td style={tdStyle}>{d.phone || '—'}</td>
                <td style={tdStyle}>{branch?.name || '—'}</td>
                <td style={tdStyle}><span style={{display:'inline-block', width:'18px', height:'18px', borderRadius:'4px', backgroundColor:d.color, border:'1px solid #ddd'}}/></td>
                <td style={tdStyle}>
                  <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700',
                    backgroundColor: d.isActive ? '#d1fae5' : '#fee2e2',
                    color: d.isActive ? '#065f46' : '#991b1b'}}>
                    {d.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={tdStyle}>
                  {stats ? (
                    <div style={{ fontSize:'0.8rem' }}>
                      <span style={{ padding:'3px 10px', borderRadius:12, fontWeight:700,
                        background: stats.successRate >= 85 ? '#dcfce7' : stats.successRate >= 70 ? '#e0f2fe' : '#fee2e2',
                        color:      stats.successRate >= 85 ? '#166534' : stats.successRate >= 70 ? '#075985' : '#991b1b' }}>
                        {stats.successRate}%
                      </span>
                      <div style={{ fontSize:'0.7rem', color:'#666', marginTop:2 }}>
                        {stats.total} cases{stats.avgSatisfaction != null ? ` · ⭐${stats.avgSatisfaction}` : ''}
                      </div>
                    </div>
                  ) : <span style={{ color:'#aaa', fontSize:'0.75rem' }}>No data</span>}
                </td>
                <td style={tdStyle}>
                  <button onClick={()=>handleEdit(d)} style={{padding:'5px 12px', backgroundColor:'#0582ca', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600', marginRight:'6px'}}>Edit</button>
                  {d.isActive && <button onClick={()=>handleDelete(d._id)} style={{padding:'5px 12px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600'}}>Deactivate</button>}
                </td>
              </tr>
            );
          })}
          {doctors.length === 0 && (
            <tr><td colSpan="9" style={{...tdStyle, textAlign:'center', color:'#aaa', padding:'30px'}}>No doctors yet — add one above</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DoctorMasterPage;
